/**
 * Grafo de circulación (componente 6 de la auditoría del v6, adaptado): nodos en los puntos de
 * las vías, en sus cruces, en los empalmes en T y en las bocas de las estaciones; aristas
 * dirigidas en las vías de un sentido. Rutas más cortas con Dijkstra y cola de prioridad.
 */

import type { Punto, Via } from '../dominio/espacio';
import { intersectarSegmentos, proyectarEnSegmento } from './geometria';

export interface NodoCirculacion {
  x: number;
  z: number;
  adyacentes: { a: number; d: number }[];
}

export interface Tramo {
  via: Via;
  a: Punto;
  b: Punto;
  unSentido: boolean;
}

export interface GrafoCirculacion {
  nodos: NodoCirculacion[];
  tramos: Tramo[];
  buscar(pt: Punto): number | undefined;
}

/** Distancia a la que el extremo de una vía se empalma con otra (empalme en T). */
export const EMPALME_M = 0.7;
/** Distancia a la que una boca se considera sobre un tramo. */
const ANCLA_M = 0.06;

export function tramosVehiculares(vias: readonly Via[]): Tramo[] {
  return tramosDe(vias, 'vehicular');
}

/**
 * Tramos por los que se mueve cada circulación: los autos, por las vías vehiculares; las personas,
 * por todas (también cruzan y caminan por las calzadas, como en el v6).
 */
export function tramosDe(vias: readonly Via[], circulacion: 'vehicular' | 'peatonal'): Tramo[] {
  const tramos: Tramo[] = [];
  for (const via of vias) {
    if (via.puntos.length < 2) continue;
    if (circulacion === 'vehicular' && via.tipo === 'peatonal') continue;
    for (let i = 0; i < via.puntos.length - 1; i++) {
      tramos.push({
        via,
        a: via.puntos[i]!,
        b: via.puntos[i + 1]!,
        unSentido: via.tipo === 'vehicular-un-sentido',
      });
    }
  }
  return tramos;
}

export function construirGrafo(vias: readonly Via[], anclas: readonly Punto[]): GrafoCirculacion {
  const nodos: NodoCirculacion[] = [];
  const indice = new Map<string, number>();
  const clave = (x: number, z: number) => `${Math.round(x * 50)},${Math.round(z * 50)}`;
  const nodo = (pt: Punto) => {
    const k = clave(pt.x, pt.z);
    let i = indice.get(k);
    if (i === undefined) {
      i = nodos.length;
      indice.set(k, i);
      nodos.push({ x: pt.x, z: pt.z, adyacentes: [] });
    }
    return i;
  };
  const enlazar = (a: number, b: number, unSentido: boolean) => {
    if (a === b) return;
    const A = nodos[a]!;
    const B = nodos[b]!;
    const d = Math.hypot(A.x - B.x, A.z - B.z);
    if (!A.adyacentes.some((e) => e.a === b)) A.adyacentes.push({ a: b, d });
    if (!unSentido && !B.adyacentes.some((e) => e.a === a)) B.adyacentes.push({ a, d });
  };

  const tramos = tramosVehiculares(vias);
  const puntos: Punto[][] = tramos.map(() => []);
  const extra: [Punto, Punto][] = [];

  // Cruces entre tramos.
  for (let i = 0; i < tramos.length; i++) {
    for (let j = i + 1; j < tramos.length; j++) {
      const s = tramos[i]!;
      const r = tramos[j]!;
      const hit = intersectarSegmentos(s.a, s.b, r.a, r.b);
      if (hit) {
        puntos[i]!.push(hit.p);
        puntos[j]!.push(hit.p);
      }
    }
  }
  // Extremos de una vía que tocan otra (empalmes en T).
  const viasVehiculares = vias.filter((v) => v.tipo !== 'peatonal' && v.puntos.length >= 2);
  for (const via of viasVehiculares) {
    const extremos = [via.puntos[0]!, via.puntos[via.puntos.length - 1]!];
    for (const e of extremos) {
      tramos.forEach((s, i) => {
        if (s.via === via && (s.a === e || s.b === e)) return;
        const pr = proyectarEnSegmento(e, s.a, s.b);
        if (pr.d < EMPALME_M) {
          puntos[i]!.push(pr.q);
          if (pr.d > 0.01) extra.push([e, pr.q]);
        }
      });
    }
  }
  // Bocas de estaciones y marcadores.
  for (const ancla of anclas) {
    tramos.forEach((s, i) => {
      if (proyectarEnSegmento(ancla, s.a, s.b).d < ANCLA_M) puntos[i]!.push(ancla);
    });
  }
  tramos.forEach((s, i) => {
    const todos = [s.a, s.b, ...puntos[i]!]
      .map((pt) => ({ pt, t: proyectarEnSegmento(pt, s.a, s.b).t }))
      .sort((x, y) => x.t - y.t);
    for (let k = 0; k < todos.length - 1; k++)
      enlazar(nodo(todos[k]!.pt), nodo(todos[k + 1]!.pt), s.unSentido);
  });
  for (const [a, b] of extra) enlazar(nodo(a), nodo(b), false);

  return { nodos, tramos, buscar: (pt) => indice.get(clave(pt.x, pt.z)) };
}

/** Dijkstra con montículo binario. Devuelve distancias y predecesores desde `origen`. */
export function caminosMinimos(
  g: GrafoCirculacion,
  origen: number,
): { dist: Float64Array; previo: Int32Array } {
  const n = g.nodos.length;
  const dist = new Float64Array(n).fill(Infinity);
  const previo = new Int32Array(n).fill(-1);
  dist[origen] = 0;
  const monticulo: [number, number][] = [[0, origen]];
  const subir = (i: number) => {
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (monticulo[p]![0] <= monticulo[i]![0]) break;
      [monticulo[p], monticulo[i]] = [monticulo[i]!, monticulo[p]!];
      i = p;
    }
  };
  const bajar = (i: number) => {
    for (;;) {
      const l = 2 * i + 1;
      const r = l + 1;
      let m = i;
      if (l < monticulo.length && monticulo[l]![0] < monticulo[m]![0]) m = l;
      if (r < monticulo.length && monticulo[r]![0] < monticulo[m]![0]) m = r;
      if (m === i) break;
      [monticulo[m], monticulo[i]] = [monticulo[i]!, monticulo[m]!];
      i = m;
    }
  };
  while (monticulo.length > 0) {
    const [d, u] = monticulo[0]!;
    const ultimo = monticulo.pop()!;
    if (monticulo.length > 0) {
      monticulo[0] = ultimo;
      bajar(0);
    }
    if (d > dist[u]!) continue;
    for (const { a, d: w } of g.nodos[u]!.adyacentes) {
      const nd = d + w;
      if (nd < dist[a]!) {
        dist[a] = nd;
        previo[a] = u;
        monticulo.push([nd, a]);
        subir(monticulo.length - 1);
      }
    }
  }
  return { dist, previo };
}

export function reconstruirCamino(previo: Int32Array, destino: number): number[] {
  const camino: number[] = [];
  for (let v = destino; v !== -1; v = previo[v]!) camino.unshift(v);
  return camino;
}

/** Punto más cercano sobre cualquier tramo vehicular. */
export function puntoMasCercano(
  tramos: readonly Tramo[],
  pt: Punto,
): { q: Punto; d: number } | null {
  let mejor: { q: Punto; d: number } | null = null;
  for (const s of tramos) {
    const pr = proyectarEnSegmento(pt, s.a, s.b);
    if (!mejor || pr.d < mejor.d) mejor = { q: pr.q, d: pr.d };
  }
  return mejor;
}

/** Primer cruce del rayo `origen + dir·t` (0,3 m < t ≤ máximo) con algún tramo. */
export function rayoAVia(
  tramos: readonly Tramo[],
  origen: Punto,
  dir: Punto,
  maximo = 12,
): Punto | null {
  const lejos = { x: origen.x + dir.x * maximo, z: origen.z + dir.z * maximo };
  let mejor: { t: number; p: Punto } | null = null;
  for (const s of tramos) {
    const hit = intersectarSegmentos(origen, lejos, s.a, s.b);
    if (hit && hit.t * maximo > 0.3 && (!mejor || hit.t < mejor.t)) mejor = { t: hit.t, p: hit.p };
  }
  return mejor ? mejor.p : null;
}
